// Central data source for the site
export const SERVICES = [
  {
    id: "cybersecurity",
    title: "Cybersecurity",
    icon: "shield-check",
    tagline: "Zero-trust security across identity, network, endpoint & cloud.",
    desc: "End-to-end security consulting, architecture and operations — from ISO 27001 & DPDP readiness to Zero-Trust design, ransomware resilience and 24×7 threat monitoring.",
    highlights: [
      "Cyber Risk Assessments & board-ready roadmaps",
      "ISO 27001, DPDP, SOC 2, RBI, SEBI, CERT-In readiness",
      "Zero-Trust architecture (NIST SP 800-207)",
      "Identity, endpoint, network & data protection",
    ],
    tone: "primary",
  },
  {
    id: "it-infrastructure",
    title: "IT Infrastructure",
    icon: "server",
    tagline: "Modern, resilient hybrid infrastructure that scales securely.",
    desc: "Design, deploy and modernize server, storage and virtualization estates. Bring legacy environments to enterprise-grade reliability with security engineered from the ground up.",
    highlights: [
      "Server, storage & virtualization design",
      "Data-center consolidation & expansion",
      "High-availability & business-continuity design",
      "Endpoint & peripheral standardization",
    ],
    tone: "accent",
  },
  {
    id: "backup",
    title: "Backup & Disaster Recovery",
    icon: "database",
    tagline: "Ransomware-resilient backup and rapid recovery, tested & audited.",
    desc: "Immutable, air-gapped backup with orchestrated recovery. RPO/RTO aligned to business criticality — validated by regular restore drills and DR run-books.",
    highlights: [
      "Immutable & cyber-resilient backup",
      "Email, endpoint & cloud data backup",
      "Disaster Recovery orchestration & drills",
      "Regulatory retention & compliance support",
    ],
    tone: "secondary",
  },
  {
    id: "cloud",
    title: "Cloud Solutions",
    icon: "cloud",
    tagline: "Secure cloud adoption on AWS, Azure and Indian sovereign cloud.",
    desc: "From landing-zone design to migration, cost governance and cloud-native security posture — engineered to your compliance regime and business economics.",
    highlights: [
      "AWS, Microsoft Azure & NxtGen Cloud design",
      "Migration, modernization & landing zones",
      "Cloud Security Posture Management (CSPM/CNAPP)",
      "Cost governance & FinOps",
    ],
    tone: "primary",
  },
  {
    id: "managed-security",
    title: "Managed Security Services",
    icon: "activity",
    tagline: "24×7 SOC, SIEM/SOAR and OEM product management.",
    desc: "We operate your security stack — day-to-day monitoring, incident response and lifecycle management of your firewalls, EDR, PAM, DLP and cloud security tooling.",
    highlights: [
      "24×7 monitoring, detection & response",
      "SIEM/SOAR operations & tuning",
      "OEM product management (firewall, EDR, PAM, DLP)",
      "Continuous compliance & vulnerability management",
    ],
    tone: "secondary",
  },
];

export const INDUSTRIES = [
  { id: "manufacturing", title: "Manufacturing", icon: "factory", desc: "OT/IT convergence, ISA/IEC 62443 alignment, plant floor resilience.",
    image: "https://images.unsplash.com/photo-1565043666747-69f6646db940?w=1400&auto=format&fit=crop&q=80",
    accent: "#0F4C81" },
  { id: "healthcare", title: "Healthcare", icon: "heart-pulse", desc: "PHI protection, HIPAA-aligned controls, medical device security.",
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1400&auto=format&fit=crop&q=80",
    accent: "#38BDF8" },
  { id: "pharma", title: "Pharmaceutical", icon: "pill", desc: "GxP, IP protection, R&D data integrity & regulatory compliance.",
    image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=1400&auto=format&fit=crop&q=80",
    accent: "#F97316" },
  { id: "banking", title: "Banking & Finance", icon: "landmark", desc: "RBI, SEBI, PCI-DSS, cyber resilience for critical financial systems.",
    image: "https://images.unsplash.com/photo-1541354329998-f4d9a9f9297f?w=1400&auto=format&fit=crop&q=80",
    accent: "#0F4C81" },
  { id: "education", title: "Education", icon: "graduation-cap", desc: "Student data privacy (DPDP), campus network, LMS security.",
    image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1400&auto=format&fit=crop&q=80",
    accent: "#38BDF8" },
  { id: "it-ites", title: "IT & ITES", icon: "cpu", desc: "SOC 2, ISO 27001, client-mandated security & vendor risk.",
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=1400&auto=format&fit=crop&q=80",
    accent: "#F97316" },
  { id: "engineering", title: "Engineering", icon: "hard-hat", desc: "IP-heavy design environments, CAD data & partner-supply security.",
    image: "https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=1400&auto=format&fit=crop&q=80",
    accent: "#0F4C81" },
  { id: "retail", title: "Retail", icon: "shopping-bag", desc: "POS security, e-commerce, customer data & PCI compliance.",
    image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1400&auto=format&fit=crop&q=80",
    accent: "#F97316" },
  { id: "government", title: "Government", icon: "building-2", desc: "CERT-In empanelment readiness, MeitY guidelines, secure e-gov.",
    image: "https://images.unsplash.com/photo-1541872703-74c5e44368f9?w=1400&auto=format&fit=crop&q=80",
    accent: "#0F4C81" },
];

// Rich detail per industry — used by the interactive Industry modal
export const INDUSTRY_DETAILS = {
  manufacturing: {
    overview: "Manufacturers face a growing collision of IT and OT — connected plants, IIoT sensors and legacy control systems that were never designed to be secure. A single ransomware event can halt production for weeks.",
    dataAssets: ["Production plans & recipes", "PLC/SCADA & MES data", "Vendor & supplier records", "IP: drawings, tooling designs"],
    itCompliance: ["ISO 27001", "ISO/IEC 22301 (BCM)", "CERT-In directives"],
    dataCompliance: ["DPDP Act, 2023 (employee & vendor data)", "IP protection (trade secrets)"],
    securityCompliance: ["ISA/IEC 62443 (OT security)", "NIST CSF 2.0", "NERC CIP (where applicable)"],
    controls: [
      "IT/OT network segmentation (Purdue model)",
      "Immutable, air-gapped backups for MES & ERP",
      "Anomaly-based OT monitoring (Nozomi / Claroty class)",
      "Least-privilege access + PAM for plant SCADA",
      "Ransomware-resilient DR for production systems",
      "Vendor & remote-access governance",
    ],
    threats: ["Ransomware halting production", "IP theft & industrial espionage", "Supply-chain compromise", "Nation-state OT attacks"],
  },
  healthcare: {
    overview: "Hospitals, diagnostic chains and health-tech firms handle uniquely sensitive personal health data, medical device integrations and 24×7 clinical dependencies where availability is a patient-safety issue.",
    dataAssets: ["Electronic Health Records (EHR/EMR)", "Diagnostic imaging (DICOM/PACS)", "Insurance & billing data", "Medical device telemetry"],
    itCompliance: ["ISO 27001", "ISO 27799 (health)", "HITRUST (for US-facing)"],
    dataCompliance: ["DPDP Act, 2023 (Sensitive Personal Data)", "HIPAA (US clients)", "Consent & data-principal rights"],
    securityCompliance: ["NIST CSF 2.0", "IEC 80001-1 (medical device networks)", "CERT-In incident reporting"],
    controls: [
      "Encryption of PHI at rest & in transit",
      "MFA + SSO for clinicians & billing staff",
      "Medical device network segmentation",
      "DLP for exports of patient records",
      "Immutable clinical-data backups & DR drills",
      "Awareness training against phishing/pretexting",
    ],
    threats: ["Ransomware disrupting patient care", "PHI theft & extortion", "Insider data leakage", "Medical device compromise"],
  },
  pharma: {
    overview: "Pharma organizations combine highly regulated manufacturing, IP-critical R&D and global supply chains — a compromise can leak formulations, corrupt clinical data or invalidate audits.",
    dataAssets: ["Clinical trial data", "Drug formulations & IP", "GxP records & batch logs", "Regulatory submissions"],
    itCompliance: ["ISO 27001", "ISO 9001", "GxP (GMP/GLP/GCP)"],
    dataCompliance: ["21 CFR Part 11 (electronic records)", "DPDP Act, 2023", "EU GDPR (EU operations)"],
    securityCompliance: ["NIST CSF 2.0", "ISA/IEC 62443 (manufacturing OT)", "CERT-In directions"],
    controls: [
      "Data integrity controls (ALCOA+)",
      "Immutable audit trails on GxP systems",
      "IP-focused DLP & rights management",
      "Zero-trust access for R&D repositories",
      "OT security on packaging & filling lines",
      "Third-party CRO & CMO risk management",
    ],
    threats: ["IP exfiltration", "Data-integrity tampering", "Regulatory audit failure", "Ransomware on GxP systems"],
  },
  banking: {
    overview: "Banks, NBFCs, insurance and market intermediaries operate under some of the strictest cyber-security mandates in India — with rising real-time payment risk and phishing-driven fraud.",
    dataAssets: ["Customer KYC & financial records", "Transaction logs & payment data", "Core banking systems", "Loan & credit files"],
    itCompliance: ["ISO 27001", "ISO 22301 (BCM)", "ITGC / SOX (for listed)"],
    dataCompliance: ["DPDP Act, 2023", "RBI Master Directions on IT", "SEBI System Audit Framework"],
    securityCompliance: [
      "RBI Cyber Security Framework (banks, UCBs, NBFCs, payment SOs)",
      "SEBI Cyber Security & Cyber Resilience Framework",
      "PCI-DSS v4",
      "CERT-In incident reporting",
    ],
    controls: [
      "MFA for internet & mobile banking",
      "Fraud analytics & transaction monitoring",
      "SOC with 24×7 monitoring (mandated)",
      "Data leakage prevention on customer PII",
      "Segregation of duties + PAM on core banking",
      "Table-topped incident response & DR drills",
    ],
    threats: ["Financial fraud & account takeover", "Ransomware on core systems", "Regulatory penalty for non-compliance", "Third-party breach via fintech APIs"],
  },
  education: {
    overview: "Schools, universities and ed-tech platforms hold minors' data at scale — with hybrid campus networks, BYOD, LMS platforms and rising phishing against staff and students.",
    dataAssets: ["Student records & grades", "Parent/guardian PII", "Research data", "Financial aid & fees"],
    itCompliance: ["ISO 27001", "UGC / AICTE IT directives", "NEP-aligned digital governance"],
    dataCompliance: ["DPDP Act, 2023 (child-consent provisions)", "FERPA (US alignments)"],
    securityCompliance: ["NIST CSF 2.0", "CIS Controls v8", "CERT-In directions"],
    controls: [
      "Identity & SSO across LMS + admin apps",
      "MFA for staff & sensitive systems",
      "Campus network segmentation (staff/student/guest)",
      "Content filtering & safe browsing",
      "Awareness programs for staff & students",
      "Backup of research and administrative data",
    ],
    threats: ["Phishing & credential theft", "LMS/data breaches", "Student data misuse", "Ransomware disrupting exams"],
  },
  "it-ites": {
    overview: "IT services, SaaS and BPM firms face client-mandated security (SOC 2, ISO 27001) and privacy obligations across geographies — often as a precondition to closing enterprise deals.",
    dataAssets: ["Source code & IP", "Customer production data", "Employee PII", "Client contract & delivery records"],
    itCompliance: ["ISO 27001", "ISO 27017 (cloud)", "ISO 27018 (cloud PII)"],
    dataCompliance: ["DPDP Act, 2023", "EU GDPR", "US State privacy laws (CCPA)"],
    securityCompliance: [
      "SOC 2 (Type I & II)",
      "PCI-DSS (payment handling)",
      "HIPAA / HITRUST (healthcare clients)",
      "CERT-In incident reporting",
    ],
    controls: [
      "Secure SDLC + code review + SAST/DAST",
      "Multi-tenant isolation & cloud CSPM",
      "Zero-trust access to production",
      "Client-data segregation & encryption",
      "Vendor risk management (TPRM)",
      "Continuous compliance evidence collection",
    ],
    threats: ["Client audit failure", "Source code / IP theft", "Multi-tenant data leak", "Insider threat"],
  },
  engineering: {
    overview: "Engineering, EPC and design firms are IP-heavy — CAD/BIM models, project bids and partner drawings are prime targets for both nation-state and competitor-driven espionage.",
    dataAssets: ["CAD/CAM/BIM designs", "Project bids & pricing", "Partner & sub-contractor data", "Regulatory submissions"],
    itCompliance: ["ISO 27001", "ISO 9001", "ISO 19650 (BIM)"],
    dataCompliance: ["DPDP Act, 2023", "Contractual IP obligations"],
    securityCompliance: ["NIST CSF 2.0", "CIS Controls v8", "CERT-In directions"],
    controls: [
      "IRM/DRM on design files",
      "DLP & controlled sharing to partners",
      "Zero-trust access to project vaults",
      "Endpoint hardening + EDR",
      "Immutable backup of design repositories",
      "Third-party & sub-contractor risk assessment",
    ],
    threats: ["IP theft & industrial espionage", "Bid-data leakage", "Ransomware on design vaults", "Partner-supply compromise"],
  },
  retail: {
    overview: "Retailers process card data, run loyalty programs, operate omni-channel platforms and POS estates — all high-value targets for card skimming, credential stuffing and web-skimming.",
    dataAssets: ["Card & payment data", "Loyalty program PII", "E-commerce customer accounts", "Vendor & inventory data"],
    itCompliance: ["ISO 27001", "ISO 22301 (BCM)"],
    dataCompliance: ["DPDP Act, 2023", "EU GDPR (cross-border customers)"],
    securityCompliance: ["PCI-DSS v4", "NIST CSF 2.0", "CERT-In directions"],
    controls: [
      "PCI-DSS scoped network segmentation",
      "P2PE / tokenization for card flows",
      "Web-app firewall + bot management on e-commerce",
      "POS hardening & centralized patch mgmt",
      "MFA for admin portals & marketing tools",
      "Fraud analytics on loyalty & payments",
    ],
    threats: ["Card-data breach & PCI fines", "E-skimming (Magecart)", "Account-takeover on customer portals", "POS malware"],
  },
  government: {
    overview: "Central, state and PSU entities are moving to digital citizen services, cloud and open-network stacks — with heightened threat from state-linked actors and strict CERT-In & MeitY controls.",
    dataAssets: ["Citizen PII & Aadhaar-linked records", "Case files & legal records", "Financial disbursement data", "Critical infrastructure data"],
    itCompliance: ["ISO 27001", "MeitY Cloud Empanelment (MeghRaj)", "e-Gov standards & GIGW"],
    dataCompliance: ["DPDP Act, 2023", "Right to Information alignment", "Aadhaar Regulations"],
    securityCompliance: [
      "CERT-In empanelment & directions",
      "NIC / NCIIPC guidelines (CII)",
      "STQC / MeitY audit requirements",
      "NIST CSF 2.0",
    ],
    controls: [
      "Aadhaar-vault architecture where applicable",
      "Data residency in Indian sovereign cloud",
      "Zero-trust for administrative access",
      "Continuous vulnerability assessment (VA/PT)",
      "24×7 SOC with SIEM/SOAR",
      "Log retention (180 days per CERT-In)",
    ],
    threats: ["Nation-state attacks", "Ransomware on citizen services", "Data-leak / political exposure", "Supply-chain compromise via vendors"],
  },
};

export const COMPLIANCES = [
  { code: "ISO 27001",
    subtitle: "Information Security Management System",
    desc: "Global standard for Information Security Management Systems (ISMS). We deliver gap analysis, control design, internal audit and Stage-1/2 audit support.",
    scope: "Global · Certifiable",
    highlights: ["114 controls in ISO 27002:2022", "Statement of Applicability", "Internal audit + management review", "Stage-1 & Stage-2 external audit support"],
  },
  { code: "DPDP",
    subtitle: "Digital Personal Data Protection Act, 2023",
    desc: "India's Digital Personal Data Protection Act, 2023. Consent, data-principal rights, DPO advisory, breach response readiness.",
    scope: "India · Mandatory",
    highlights: ["Consent architecture", "Data-principal rights fulfilment", "Data Protection Officer (DPO) advisory", "Breach notification & response"],
  },
  { code: "SOC 2",
    subtitle: "Trust Services Criteria (AICPA)",
    desc: "Trust Services Criteria for SaaS & service providers — Security, Availability, Confidentiality, Processing Integrity & Privacy.",
    scope: "Global · Type I & II",
    highlights: ["5 Trust Services Criteria", "Continuous evidence collection", "Type I & Type II readiness", "SOC 2 attestation support"],
  },
  { code: "RBI",
    subtitle: "Cyber Security Framework",
    desc: "Reserve Bank of India cyber security framework for banks, NBFCs, UCBs and payment system operators.",
    scope: "India · Mandatory (BFSI)",
    highlights: ["Cyber Crisis Management Plan", "Cyber Security Operation Center (CSOC)", "IS Audit & VAPT", "Board-level cyber governance"],
  },
  { code: "SEBI",
    subtitle: "Cyber Security & Resilience Framework",
    desc: "SEBI Cyber Security & Cyber Resilience framework for regulated market intermediaries.",
    scope: "India · Mandatory (Capital Markets)",
    highlights: ["Cyber Capability Index (CCI)", "Quarterly VAPT reporting", "SOC monitoring", "IT strategy committee readiness"],
  },
  { code: "CERT-In",
    subtitle: "Incident Reporting Directions",
    desc: "Directions on incident reporting, log retention (180 days) and SBOM. Program build-out & audit readiness.",
    scope: "India · Mandatory",
    highlights: ["6-hour incident reporting", "180-day log retention", "SBOM & synchronisation to NTP", "Empanelled auditor support"],
  },
  { code: "Zero Trust",
    subtitle: "Zero-Trust Architecture",
    desc: "Zero-trust architecture: verify explicitly, apply least privilege and assume breach across identity, network, endpoint and data.",
    scope: "Global · Architectural",
    highlights: ["Identity-centric access", "Micro-segmentation", "Continuous verification", "Assume-breach mindset"],
  },
  { code: "NIST CSF",
    subtitle: "Cybersecurity Framework 2.0",
    desc: "NIST Cybersecurity Framework 2.0 — Govern, Identify, Protect, Detect, Respond, Recover.",
    scope: "Global · Reference",
    highlights: ["6 core functions", "Profiles & tiers", "Business-aligned outcomes", "Regulator-friendly mapping"],
  },
];

export const PARTNERS = [
  { name: "AWS", logo: "https://customer-assets.emergentagent.com/job_techmate-security/artifacts/seeykmvf_AWS%20logo.webp" },
  { name: "Microsoft Azure", logo: "https://customer-assets.emergentagent.com/job_techmate-security/artifacts/mstv1ywf_AZURE.png" },
  { name: "Fortinet", logo: "https://customer-assets.emergentagent.com/job_techmate-security/artifacts/gelrm612_fortinet-logo.jpg" },
  { name: "Trellix", logo: "https://customer-assets.emergentagent.com/job_techmate-security/artifacts/vt7e6nob_Trellix_NEW_1.webp" },
  { name: "Sectona", logo: "https://customer-assets.emergentagent.com/job_techmate-security/artifacts/db3g6jij_Sectona-Logo.webp" },
  { name: "Zscaler", logo: null },
  { name: "NxtGen Cloud", logo: null },
];

// OEM products we architect, deliver and manage. Feature points sourced from vendor materials.
export const OEM_PRODUCTS = [
  {
    id: "fortinet",
    name: "Fortinet",
    logo: "https://customer-assets.emergentagent.com/job_techmate-security/artifacts/gelrm612_fortinet-logo.jpg",
    category: "Network Security · SASE · SD-WAN",
    tagline: "Security-driven networking, built on the Fortinet Security Fabric.",
    features: [
      "FortiGate NGFW — consolidated firewall, VPN, IPS & SSL inspection",
      "Secure SD-WAN with integrated NGFW & application steering",
      "FortiClient EMS with Zero-Trust Network Access (ZTNA)",
      "FortiAnalyzer & FortiManager centralized visibility",
      "AI-driven FortiGuard threat intelligence",
    ],
    color: "#EE3124",
  },
  {
    id: "trellix",
    name: "Trellix",
    logo: "https://customer-assets.emergentagent.com/job_techmate-security/artifacts/vt7e6nob_Trellix_NEW_1.webp",
    category: "XDR · Endpoint · Email · DLP",
    tagline: "Living Security — an intelligent, adaptive XDR platform.",
    features: [
      "Trellix XDR — unified detection across endpoint, email & network",
      "Endpoint Security (HX/ENS) with behavioural & GenAI analytics",
      "Advanced Email Security & phishing defense",
      "Data Loss Prevention (DLP) with policy analytics",
      "Threat Intelligence Exchange & MITRE ATT&CK mapping",
    ],
    color: "#111827",
  },
  {
    id: "sectona",
    name: "Sectona",
    logo: "https://customer-assets.emergentagent.com/job_techmate-security/artifacts/db3g6jij_Sectona-Logo.webp",
    category: "Privileged Access Management",
    tagline: "Modern PAM engineered for hybrid & multi-cloud enterprises.",
    features: [
      "Privileged Account Vaulting with credential rotation",
      "Session Management with real-time monitoring & recording",
      "Just-In-Time (JIT) privilege elevation",
      "Endpoint Privilege Management (EPM)",
      "Secrets management for DevOps & workloads",
    ],
    color: "#0846FF",
  },
  {
    id: "zscaler",
    name: "Zscaler",
    logo: null,
    category: "Zero Trust · SSE · SASE",
    tagline: "The Zscaler Zero Trust Exchange — secure any user, workload or device.",
    features: [
      "Zscaler Internet Access (ZIA) — cloud secure web gateway",
      "Zscaler Private Access (ZPA) — VPN-less ZTNA",
      "Cloud Access Security Broker (CASB) & SaaS security",
      "Cloud DLP with inline data protection",
      "Digital Experience Monitoring (ZDX)",
    ],
    color: "#0072F5",
  },
  {
    id: "azure",
    name: "Microsoft Azure",
    logo: "https://customer-assets.emergentagent.com/job_techmate-security/artifacts/mstv1ywf_AZURE.png",
    category: "Cloud · Security · Compliance",
    tagline: "Enterprise cloud with integrated identity, security & compliance.",
    features: [
      "Azure landing zones, migration & modernization",
      "Microsoft Sentinel cloud-native SIEM & SOAR",
      "Microsoft Defender for Cloud (CSPM / CWPP)",
      "Microsoft Entra ID for identity & conditional access",
      "Microsoft Purview for data governance & DLP",
    ],
    color: "#0078D4",
  },
  {
    id: "aws",
    name: "AWS",
    logo: "https://customer-assets.emergentagent.com/job_techmate-security/artifacts/seeykmvf_AWS%20logo.webp",
    category: "Cloud · Security · Resilience",
    tagline: "Well-Architected cloud with security by design.",
    features: [
      "AWS landing zones & Control Tower design",
      "GuardDuty, Security Hub & AWS Config for continuous compliance",
      "IAM, KMS & Secrets Manager for identity & keys",
      "AWS WAF & Shield for edge protection",
      "Backup, S3 Object Lock & DR patterns",
    ],
    color: "#FF9900",
  },
  {
    id: "nxtgen",
    name: "NxtGen Cloud",
    logo: null,
    category: "Indian Sovereign Cloud",
    tagline: "MeitY-empanelled cloud with in-country data residency.",
    features: [
      "Enterprise IaaS with data residency in India",
      "Managed Disaster-Recovery-as-a-Service (DRaaS)",
      "Compliance-ready hosting (ISO, PCI, DPDP)",
      "Managed private & hybrid cloud",
      "24×7 SOC-backed operations",
    ],
    color: "#EA580C",
  },
];

export const TIMELINE = [
  { step: 1, title: "Business Profile", desc: "We capture your industry, size, geography and critical business processes.", icon: "briefcase" },
  { step: 2, title: "Cyber Assessment", desc: "A short, executive-level survey across identity, data, cloud, network and resilience.", icon: "clipboard-list" },
  { step: 3, title: "Compliance Review", desc: "We map your posture to ISO 27001, DPDP, SOC 2 and applicable regulators.", icon: "badge-check" },
  { step: 4, title: "Cyber Score", desc: "Your organization receives a 0–100 Cyber Score benchmarked against industry peers.", icon: "gauge" },
  { step: 5, title: "Executive Report", desc: "A one-page Cybersecurity Readiness Report with heatmap, radar and a prioritized roadmap.", icon: "file-text" },
  { step: 6, title: "Consultant Readout", desc: "A 45-minute strategy session with a Techmate security architect — complimentary.", icon: "users" },
];

export const TESTIMONIALS = [
  { name: "CISO, Listed BFSI", quote: "Techmate helped us close a SEBI audit finding in eight weeks. Their consultants speak the language of both the board and the SOC.", industry: "Banking & Finance" },
  { name: "VP-IT, Pharma Enterprise", quote: "The Cyber Risk Navigator report was the clearest one-page view of our exposure I have ever received.", industry: "Pharmaceutical" },
  { name: "COO, Manufacturing Group", quote: "Their zero-trust architecture blueprint gave our plants ransomware resilience without disrupting production.", industry: "Manufacturing" },
  { name: "Head of IT, SaaS Scale-up", quote: "We passed SOC 2 Type II on the first attempt. Techmate acted like an extension of our team.", industry: "IT & ITES" },
];

export const FAQS = [
  { q: "What makes Techmate different?", a: "We are a consulting-led cybersecurity partner — every engagement begins with your business risk, compliance obligations and board priorities. Our recommendations are vendor-neutral, framework-led and mapped to outcomes your leadership will measure." },
  { q: "Is the Cyber Risk Navigator™ really free?", a: "Yes. The 13-question assessment and the executive readiness report are complimentary. There is no obligation to engage after the readout." },
  { q: "Which compliance frameworks do you support?", a: "ISO 27001, SOC 2 (Type I & II), DPDP Act 2023, RBI, SEBI, CERT-In, NIST CSF 2.0, PCI-DSS and Zero-Trust architecture (NIST SP 800-207)." },
  { q: "Do you work with small and mid-size businesses?", a: "Absolutely — we tailor programs for MSME, mid-market and enterprise. Our Managed Security offering is designed for teams without a full SOC." },
  { q: "Where are you based? Do you serve pan-India?", a: "Our headquarters are in Pune (Shivajinagar). We deliver engagements across India and to Indian subsidiaries of global organizations." },
  { q: "How quickly can we start?", a: "Book a consultation and we typically kick off an engagement within 5–7 business days." },
];

export const CONTACT = {
  phone: "+91 97647 96333",
  whatsapp: "+919764796333",
  email: "info@techmatesolution.net",
  address: "693, Shivajinagar, Pune 411016, Maharashtra, India",
  mapEmbed: "https://www.google.com/maps?q=Shivajinagar+Pune&output=embed",
  linkedin: "https://www.linkedin.com/company/techmate-infosec-solutions",
};

// Cyber Risk Navigator — 13 executive questions
export const ASSESSMENT_QUESTIONS = [
  { id: "q1", section: "Governance", question: "Do you have a documented cybersecurity strategy approved by leadership?", options: [
    { label: "Yes, reviewed within last 12 months", score: 4 },
    { label: "Yes, but outdated", score: 2 },
    { label: "In progress", score: 1 },
    { label: "No", score: 0 },
  ]},
  { id: "q2", section: "Governance", question: "Is a senior leader (CISO / CIO / equivalent) accountable for cybersecurity?", options: [
    { label: "Dedicated CISO", score: 4 },
    { label: "CIO / IT Head owns it", score: 3 },
    { label: "Shared responsibility", score: 1 },
    { label: "Nobody explicitly", score: 0 },
  ]},
  { id: "q3", section: "Risk", question: "When did you last conduct a formal cyber risk assessment?", options: [
    { label: "Within 12 months", score: 4 },
    { label: "1–2 years ago", score: 2 },
    { label: "More than 2 years ago", score: 1 },
    { label: "Never", score: 0 },
  ]},
  { id: "q4", section: "Compliance", question: "Which frameworks are you aligned with?", options: [
    { label: "ISO 27001 / SOC 2 certified", score: 4 },
    { label: "Aligned, not certified", score: 3 },
    { label: "Sector-mandated only (RBI/SEBI/HIPAA)", score: 2 },
    { label: "None", score: 0 },
  ]},
  { id: "q5", section: "Identity", question: "Is MFA enforced for all privileged and remote access?", options: [
    { label: "Yes, across the board", score: 4 },
    { label: "Yes, for privileged only", score: 3 },
    { label: "Partial", score: 1 },
    { label: "No", score: 0 },
  ]},
  { id: "q6", section: "Data", question: "Are critical data assets classified and access controlled?", options: [
    { label: "Fully classified with DLP", score: 4 },
    { label: "Classified, no DLP", score: 3 },
    { label: "Ad-hoc", score: 1 },
    { label: "No", score: 0 },
  ]},
  { id: "q7", section: "Endpoints", question: "Do you have modern EDR/XDR on all endpoints?", options: [
    { label: "EDR/XDR with 24×7 monitoring", score: 4 },
    { label: "EDR only", score: 3 },
    { label: "Traditional AV", score: 1 },
    { label: "No", score: 0 },
  ]},
  { id: "q8", section: "Cloud", question: "Do you have cloud security posture management (CSPM) in place?", options: [
    { label: "Yes, continuous CSPM/CNAPP", score: 4 },
    { label: "Periodic reviews", score: 2 },
    { label: "Basic config checks", score: 1 },
    { label: "No cloud / not applicable", score: 0 },
  ]},
  { id: "q9", section: "Backup", question: "Are backups immutable, tested and offline (ransomware-resilient)?", options: [
    { label: "Yes, tested restore in last 6 months", score: 4 },
    { label: "Yes, but not tested recently", score: 2 },
    { label: "Backups exist, not immutable", score: 1 },
    { label: "No formal backup strategy", score: 0 },
  ]},
  { id: "q10", section: "SOC", question: "How do you monitor for threats?", options: [
    { label: "24×7 SOC with SIEM/SOAR", score: 4 },
    { label: "Managed SOC (partner)", score: 3 },
    { label: "Business hours only", score: 1 },
    { label: "No monitoring", score: 0 },
  ]},
  { id: "q11", section: "Awareness", question: "Frequency of employee security awareness training & phishing simulations?", options: [
    { label: "Quarterly + simulations", score: 4 },
    { label: "Annual", score: 2 },
    { label: "Ad-hoc", score: 1 },
    { label: "None", score: 0 },
  ]},
  { id: "q12", section: "Incident", question: "Do you have a tested incident response plan?", options: [
    { label: "Yes, table-topped in last 12 months", score: 4 },
    { label: "Documented, not tested", score: 2 },
    { label: "Informal", score: 1 },
    { label: "No", score: 0 },
  ]},
  { id: "q13", section: "Vendor", question: "How do you assess third-party / vendor cyber risk?", options: [
    { label: "Formal TPRM program", score: 4 },
    { label: "Contract clauses + questionnaires", score: 3 },
    { label: "Ad-hoc", score: 1 },
    { label: "Not assessed", score: 0 },
  ]},
];

export const TRUST_POINTS = [
  { title: "Consulting-Led Engagements", icon: "compass" },
  { title: "Compliance-First Approach", icon: "badge-check" },
  { title: "Business-Aligned Security", icon: "briefcase" },
  { title: "Vendor-Neutral Advisory", icon: "scale" },
  { title: "Senior Solution Architects", icon: "users" },
  { title: "Enterprise-Grade Delivery", icon: "server" },
];
